import type { RequestOptions } from '@@/plugin-request/request';
import { history, type RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

/** 与后端约定：业务 code === 2 表示 token 失效，需重新登录 */
const TOKEN_FAIL_CODE = 2;
const LOGIN_PATH = '/user/login';
const TOKEN_FAIL_DEFAULT_MSG = '登录已失效，请重新登录';

function handleTokenInvalid(msg: string) {
  message.error(msg);
  localStorage.removeItem('token');
  localStorage.removeItem('currentUser');
  const redirect = encodeURIComponent(
    `${window.location.pathname}${window.location.search}`,
  );
  history.replace(`${LOGIN_PATH}?redirect=${redirect}`);
}

/** HTTP 非 2xx（如 401）时走 errorHandler，需从 response.data 再判一次业务码 */
function isTokenFailPayload(data: unknown): data is { msg?: string; message?: string; code?: number } {
  if (!data || typeof data !== 'object') return false;
  const d = data as { code?: unknown };
  return Number(d.code) === TOKEN_FAIL_CODE;
}

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      // Only enforce throw for APIs that explicitly return `success`.
      if (typeof success === 'boolean' && !success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        const body = error.response.data;
        if (isTokenFailPayload(body)) {
          const bizMsg = String(body.msg ?? body.message ?? TOKEN_FAIL_DEFAULT_MSG);
          handleTokenInvalid(bizMsg);
          return;
        }
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = localStorage.getItem('token');
      if (!token) {
        return config;
      }
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const { data } = response as unknown as { data: unknown } & ResponseStructure;

      if (isTokenFailPayload(data)) {
        handleTokenInvalid(String(data.msg ?? data.message ?? TOKEN_FAIL_DEFAULT_MSG));
        return response;
      }

      if (data?.success === false) {
        message.error('请求失败！');
      }
      return response;
    },
  ],
};
